/**
 * SocialConnectStrip — connect strip for Content Suite / any social tool.
 *
 * Shows 6 platform badges (Instagram, Facebook, LinkedIn, Twitter, YouTube, WhatsApp)
 * with connect state. Click to open a "Paste Access Token" modal.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { Instagram, Facebook, Linkedin, Twitter, Youtube, MessageCircle, Check, Plus, X, ExternalLink, Loader2, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook",  label: "Facebook",  Icon: Facebook  },
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin  },
  { key: "twitter",   label: "X / Twitter", Icon: Twitter },
  { key: "youtube",   label: "YouTube",   Icon: Youtube   },
  { key: "whatsapp",  label: "WhatsApp",  Icon: MessageCircle },
];

export default function SocialConnectStrip() {
  const { isLoggedIn } = useUser();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { platform, info }
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/social/accounts`, { withCredentials: true });
      setAccounts(r.data?.accounts || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isConnected = (key) => accounts.some((a) => a.platform === key && a.status === "active");
  const accountFor = (key) => accounts.find((a) => a.platform === key);

  const openConnect = async (platform) => {
    if (!isLoggedIn) {
      toast.error("Please log in to connect a social account");
      navigate("/login?next=/content-suite");
      return;
    }
    setToken("");
    setName("");
    setModal({ platform, info: null, loading: true });
    try {
      const r = await axios.get(`${API}/api/social/accounts/oauth-info/${platform}`);
      setModal({ platform, info: r.data, loading: false });
    } catch (e) {
      setModal({ platform, info: null, loading: false });
    }
  };

  const submit = async () => {
    if (!token.trim() || token.length < 8) {
      toast.error("Access token seems too short — check the dev console instructions.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/social/accounts/connect`,
        { platform: modal.platform, access_token: token.trim(), account_name: name.trim() || undefined },
        { withCredentials: true }
      );
      toast.success(`${modal.platform} connected!`);
      setModal(null);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not connect";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async (acct) => {
    if (!window.confirm(`Disconnect ${acct.platform}?`)) return;
    try {
      await axios.delete(`${API}/api/social/accounts/${acct.id}`, { withCredentials: true });
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <div
        className="mb-12 p-6 rounded-3xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
        data-testid="social-connect-strip"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div>
            <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Connect accounts</p>
            <h3 className="text-lg font-semibold text-[#111111]">Publish everywhere with one workflow</h3>
            <p className="text-sm text-slate-500 mt-0.5">Connect your social accounts to publish and schedule directly from MarkeeMix.</p>
          </div>
          <span className="text-xs text-slate-500">
            {loading ? "…" : `${accounts.length}/${PLATFORMS.length} connected`}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PLATFORMS.map(({ key, label, Icon }) => {
            const connected = isConnected(key);
            const acct = accountFor(key);
            return (
              <div
                key={key}
                data-testid={`connect-${key}`}
                className={`relative flex items-center gap-3 rounded-2xl border p-3.5 transition-all ${
                  connected
                    ? "border-emerald-200 bg-emerald-50/40"
                    : "border-slate-200 hover:border-[#A855F7]/40 hover:shadow-[0_4px_16px_rgba(168,85,247,0.06)]"
                }`}
              >
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  connected ? "bg-emerald-500 text-white" : "bg-[#FAF5FF] text-[#A855F7]"
                }`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111111] truncate">{label}</p>
                  {connected ? (
                    <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                      <Check className="w-3 h-3" /> {acct?.account_name || "Connected"}
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500">Not connected</p>
                  )}
                </div>
                {connected ? (
                  <button
                    onClick={() => disconnect(acct)}
                    className="opacity-40 hover:opacity-100 text-slate-500 hover:text-red-500 transition"
                    title="Disconnect"
                    data-testid={`disconnect-${key}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => openConnect(key)}
                    className="text-[11px] font-semibold text-[#A855F7] hover:text-[#7E22CE] flex items-center gap-0.5"
                    data-testid={`connect-btn-${key}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Connect
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Connect modal */}
      {modal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => !saving && setModal(null)}
          data-testid="connect-modal"
        >
          <div
            className="w-full max-w-md p-6 rounded-3xl bg-white shadow-[0_16px_48px_rgba(0,0,0,0.15)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Connect · {modal.platform}</p>
                <h3 className="text-xl font-semibold text-[#111111]">Paste your access token</h3>
              </div>
              <button
                onClick={() => setModal(null)}
                className="p-2 text-slate-400 hover:text-[#111111] rounded-full hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {modal.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#A855F7] animate-spin" />
              </div>
            ) : (
              <>
                {modal.info?.how_to && (
                  <div className="mb-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                    <p className="text-xs font-semibold text-[#111111] mb-1">How to get your token</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{modal.info.how_to}</p>
                    {modal.info.docs_url && (
                      <a
                        href={modal.info.docs_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#A855F7] hover:text-[#7E22CE]"
                      >
                        Open developer console <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}

                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Access token</label>
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="EAAG... / AQV... / xoxb-..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#A855F7] text-sm font-mono"
                  data-testid="connect-token-input"
                />
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 mt-4">Account name (optional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="@markeemix or Page name"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#A855F7] text-sm"
                  data-testid="connect-name-input"
                />

                <div className="flex items-center gap-2 mt-6">
                  <button
                    onClick={() => setModal(null)}
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={saving || !token}
                    className="flex-1 px-4 py-2.5 rounded-full bg-[#A855F7] hover:bg-[#9333EA] text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
                    data-testid="connect-submit"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {saving ? "Connecting…" : "Connect"}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-3 text-center">
                  Your token is encrypted and never shared. Only used to publish on your behalf.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
